import * as THREE from 'three';
import { boneNameIndex, normalizeBoneName } from './boneNames.js';

/**
 * A weapon, in a hand.
 *
 * Small and deliberately so: a box parented to the bone the weapon's own
 * `hitbox` names, taking its size and colour from `weapons.json`. There is no
 * weapon model in this project and no budget to make one, but a fighter holding
 * a bat has to *look* like a fighter holding a bat — a strike set that silently
 * swaps is a mechanic the player experiences as their jab having changed its
 * mind.
 *
 * Parented to the bone rather than posed every frame, so it rides the animation
 * for free and costs nothing per frame once it is on.
 */

/** One weapon's box, gripped at one end. Shared by the hand and the floor. */
function weaponBox(spec) {
  const [x, y, z] = spec.size ?? [0.06, 0.06, 0.6];
  const geometry = new THREE.BoxGeometry(x, y, z);
  // Pushed along its own length so the grip is at one end rather than the
  // middle, which is the difference between holding a bat and balancing one.
  geometry.translate(0, 0, -z / 2);
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(spec.colour ?? '#8a6a3c'),
    roughness: 0.7,
    metalness: 0.25,
  });
  return { geometry, material, length: z };
}

/** Which bone each hitbox name hangs off. The rig's own names. */
const BONES = Object.freeze({
  leftHand: 'LeftHand',
  rightHand: 'RightHand',
  leftFoot: 'LeftFoot',
  rightFoot: 'RightFoot',
});

export function createWeaponModel(fighter, { onWarn } = {}) {
  let index = new Map();
  let byName = new Map();
  function seat(body) {
    const skeleton = body?.skeleton ?? null;
    index = skeleton ? boneNameIndex(skeleton.bones) : new Map();
    byName = new Map((skeleton?.bones ?? []).map((bone) => [bone.name, bone]));
  }
  seat(fighter);

  let mesh = null;
  let geometry = null;
  let material = null;

  function clear() {
    mesh?.removeFromParent();
    geometry?.dispose();
    material?.dispose();
    mesh = null;
    geometry = null;
    material = null;
  }

  return {
    get object3D() {
      return mesh;
    },

    /**
     * Shows `weapon` in the hand its move strikes with, or nothing at all.
     *
     * Rebuilt rather than recoloured on each change: it happens a handful of
     * times a round, and a bat and a bottle are not the same shape.
     */
    show(weapon) {
      clear();
      if (!weapon) return null;

      const spec = weapon.spec ?? {};
      const bone = byName.get(index.get(normalizeBoneName(BONES[spec.hitbox] ?? ''))) ?? null;
      if (!bone) {
        // An unrigged fallback capsule has no hands. The weapon still works;
        // it is simply not drawn, which beats not booting.
        onWarn?.(`no bone for ${spec.hitbox}; ${weapon.id} is held invisibly`);
        return null;
      }

      ({ geometry, material } = weaponBox(spec));
      mesh = new THREE.Mesh(geometry, material);
      mesh.name = `weapon-${weapon.id}`;
      mesh.castShadow = true;
      // The bone's own axes, not the world's: the hand is what it rides.
      mesh.rotation.set(Math.PI / 2, 0, 0);
      bone.add(mesh);
      return mesh;
    },

    /**
     * A new body: its hands are different bones. Whatever is being held is
     * taken off the old hand; the caller shows it again on the new one.
     */
    rebind(next) {
      clear();
      seat(next);
    },

    dispose: clear,
  };
}

/**
 * Weapons lying on the floor.
 *
 * `core/weapons.js` decides where one is and who gets it; this draws them. It
 * rebuilds only when the list changes — a drop and a pickup happen a handful of
 * times a round — and spins what is lying there, because a small dark box on a
 * dark floor is invisible and a *turning* one is not. That is the only job the
 * spin has: the weapon is a point on the floor as far as the rules go.
 */
export function createWeaponDrops(ground, { config = null } = {}) {
  const group = new THREE.Group();
  group.name = 'weapon-drops';

  const drawn = new Map();
  const height = config?.pickup?.height ?? 0.12;
  let clock = 0;

  function build(item) {
    const { geometry, material, length } = weaponBox(item.weapon.spec);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = `dropped-${item.weapon.id}`;
    mesh.castShadow = true;
    // Already lying down: the box is built along its own -Z, which is
    // horizontal, so it needs **no** rotation at all. The first version turned
    // it a quarter about X and stood three weapons up on end like fence posts.
    // The offset only re-centres it, so the pivot is the middle rather than the
    // grip — a dropped bat spins about itself, not about its handle.
    const pivot = new THREE.Group();
    pivot.add(mesh);
    mesh.position.z = length / 2;
    pivot.position.set(item.x, height, item.z);
    group.add(pivot);
    return { pivot, mesh, geometry, material };
  }

  function drop(entry) {
    entry.pivot.removeFromParent();
    entry.geometry.dispose();
    entry.material.dispose();
  }

  return {
    object3D: group,

    update(dt) {
      clock += dt;
      const live = new Set();

      for (const item of ground.items) {
        live.add(item);
        let entry = drawn.get(item);
        if (!entry) {
          entry = build(item);
          drawn.set(item, entry);
        }
        entry.pivot.position.set(item.x, height, item.z);
        entry.pivot.rotation.y = clock * 0.9;
        // The last couple of seconds, blinking, so a weapon about to vanish
        // does not simply vanish.
        const fading = item.left < 2.5;
        entry.pivot.visible = !fading || Math.sin(item.left * 14) > -0.35;
      }

      for (const [item, entry] of drawn) {
        if (live.has(item)) continue;
        drop(entry);
        drawn.delete(item);
      }
    },

    dispose() {
      for (const entry of drawn.values()) drop(entry);
      drawn.clear();
      group.removeFromParent();
    },
  };
}
